import { CollectFeesButton } from 'components/CollectFeesButton'
import { useV3PositionDisplay } from 'hooks/portfolio/useV3PositionDisplay'
import { useConnection } from 'hooks/useConnection'
import type { ReactNode } from 'react'
import { V3PositionFees, V3PositionGroup } from 'types/portfolio/v3'
import { ProcessedV3Position } from 'types/subgraph'
import { formatDollarAmount } from 'utils/portfolio/formatDollar'
import { Address } from 'viem'

import { PortfolioRangeDisplay } from './PortfolioRangeDisplay'
import { PositionCTAs } from './PositionCTAs'
import { FormattedTokenBalance } from './TokenBalanceDisplay'
import { TokenPairLogo } from './TokenPairLogo'

type V3PoolGroupInfo = Pick<V3PositionGroup, 'token0' | 'token1' | 'feeTier'>

interface V3PositionItemProps {
  position: ProcessedV3Position
  fees?: V3PositionFees
  tokenPrices?: Record<Address, number>
  onCollectSuccess?: () => void
  variant: 'single' | 'grouped'
  poolGroup?: V3PoolGroupInfo
  showRange?: boolean
  showMobileRange?: boolean
  showRangePlaceholder?: boolean
}

export const V3PositionItem = ({
  position,
  fees,
  tokenPrices,
  onCollectSuccess,
  variant,
  poolGroup,
  showRange = false,
  showMobileRange,
  showRangePlaceholder = true,
}: V3PositionItemProps) => {
  const { account } = useConnection()

  const { positionValue, token0Display, token1Display, feeData, badgeConfig, addLiquidityCTA, removeLiquidityCTA } =
    useV3PositionDisplay({ position, fees, tokenPrices })

  const { fee0Amount, fee1Amount, feeValue0, feeValue1, fees0Display, fees1Display, token0, token1 } = feeData
  const hasUnclaimedFees = (fee0Amount !== '0' && fee0Amount !== '') || (fee1Amount !== '0' && fee1Amount !== '')
  const positionIdLabel = `Position #${position.nftId}`

  const positionBadge = (
    <div className="flex items-center gap-1 sm:gap-1.5 shrink-0 whitespace-nowrap">
      <span
        className="px-1.5 sm:px-1.5 py-0.5 sm:py-1 rounded-xs text-[11px] font-medium whitespace-nowrap"
        style={{ backgroundColor: badgeConfig.bgColor, color: badgeConfig.textColor }}
      >
        V3
      </span>
      <span
        className="flex gap-1 items-center px-1.5 sm:px-1.5 py-0.5 sm:py-1 rounded-xs text-[11px] font-medium whitespace-nowrap"
        style={badgeConfig.statusStyle}
      >
        <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: badgeConfig.statusCircleColor }} />
        {badgeConfig.statusText}
      </span>
    </div>
  )

  const renderBalances = (className: string) => (
    <div className={className}>
      <FormattedTokenBalance value={token0Display} />
      <FormattedTokenBalance value={token1Display} />
    </div>
  )

  const renderFees = ({
    className,
    emptyClassName = 'text-sm text-kodiak-text-secondary',
    emptyText = 'No unclaimed fees',
    withLabel = false,
  }: {
    className: string
    emptyClassName?: string
    emptyText?: string
    withLabel?: boolean
  }) => {
    if (!hasUnclaimedFees) {
      return withLabel ? null : <div className={emptyClassName}>{emptyText}</div>
    }

    return (
      <div>
        {withLabel && <div className="text-xs text-kodiak-text-primary mb-1">Unclaimed Fees:</div>}
        <div className={className}>
          <FormattedTokenBalance value={fees0Display} />
          <FormattedTokenBalance value={fees1Display} />
        </div>
      </div>
    )
  }

  const renderActions = (className: string) => (
    <div className={className}>
      <PositionCTAs primaryCTA={addLiquidityCTA} secondaryCTA={removeLiquidityCTA} />
      <CollectFeesButton
        tokenId={position.nftId}
        token0={token0}
        token1={token1}
        feeValue0={feeValue0}
        feeValue1={feeValue1}
        owner={position.owner}
        currentAccount={account}
        showTooltip={false}
        onCollectSuccess={onCollectSuccess}
        variant="portfolio"
      />
    </div>
  )

  const renderDesktopSharedColumns = (): ReactNode => (
    <>
      {renderBalances('text-sm text-[#F8FAFC] space-y-1')}
      <div className="text-kodiak-text-primary font-medium">{formatDollarAmount(positionValue)}</div>
      <div>{renderFees({ className: 'text-sm text-[#6EE7B7] space-y-1' })}</div>
      {renderActions('flex flex-col w-full gap-2')}
    </>
  )

  const shouldShowMobileRange = showMobileRange ?? showRange

  const renderMobileRange = () =>
    shouldShowMobileRange ? (
      <div className="mb-3 flex items-start justify-between gap-3">
        <div className="text-kodiak-text-secondary text-xs">Range</div>
        <div className="min-w-0">
          <PortfolioRangeDisplay
            token0Address={position.enrichedToken0.id}
            token1Address={position.enrichedToken1.id}
            lowerTick={position.sdkPosition.tickLower}
            upperTick={position.sdkPosition.tickUpper}
            currentTick={position.sdkPosition.pool.tickCurrent}
            feeTier={position.sdkPosition.pool.fee}
            poolType="V3"
            showLabelsOnMobile
          />
        </div>
      </div>
    ) : null

  const renderMobileDetails = (titleClassName: string, wrapperClassName = 'flex justify-between gap-2 flex-wrap') => (
    <div className={wrapperClassName}>
      <div>
        <div className={titleClassName}>{positionIdLabel}</div>
        {renderBalances('text-xs text-[#F8FAFC] space-y-1')}
      </div>
      {renderFees({ className: 'text-xs text-[#6EE7B7] space-y-1', withLabel: true })}
    </div>
  )

  const rangeDisplay = showRange ? (
    <PortfolioRangeDisplay
      token0Address={position.enrichedToken0.id}
      token1Address={position.enrichedToken1.id}
      lowerTick={position.sdkPosition.tickLower}
      upperTick={position.sdkPosition.tickUpper}
      currentTick={position.sdkPosition.pool.tickCurrent}
      feeTier={position.sdkPosition.pool.fee}
      poolType="V3"
    />
  ) : null

  if (variant === 'single') {
    return (
      <div className="mb-4 last:mb-0 sm:mb-0 bg-[var(--color-portfolio-filters-bg)] sm:bg-transparent rounded-sm sm:rounded-none border border-[var(--color-kodiak-slider)] sm:border-0 shadow-sm sm:shadow-none sm:border-b sm:border-b-kodiak-border-secondary sm:last:border-b-0 hover:bg-kodiak-hover-card transition-colors duration-200 overflow-hidden">
        <div className="sm:hidden p-3 space-y-4">
          <div className="flex flex-col">
            <div className="flex justify-between gap-2 space-y-3">
              {poolGroup ? (
                <div className="flex items-center space-x-3">
                  <TokenPairLogo token0={poolGroup.token0} token1={poolGroup.token1} size="md" />
                  <div className="flex flex-col gap-0.5">
                    <div className="font-medium text-kodiak-text-primary text-sm sm:text-base">
                      {poolGroup.token0.symbol}-{poolGroup.token1.symbol}
                    </div>
                    <div className="text-xs text-kodiak-portfolio-table-header-text">{poolGroup.feeTier / 10000}%</div>
                  </div>
                </div>
              ) : (
                <div />
              )}

              <div className="flex flex-col items-end gap-1.5">
                {positionBadge}
                <div className="text-kodiak-text-primary font-medium text-sm">{formatDollarAmount(positionValue)}</div>
              </div>
            </div>

            {renderMobileRange()}

            {renderMobileDetails(
              'text-xs text-kodiak-text-primary',
              'flex justify-between gap-2 flex-wrap w-full items-end'
            )}
          </div>

          {renderActions('flex flex-col gap-2')}
        </div>

        <div
          className="hidden sm:grid items-center py-3 px-6 gap-4"
          style={{
            gridTemplateColumns:
              'minmax(0, 1.55fr) minmax(0, 150px) minmax(0, 1fr) minmax(0, 0.6fr) minmax(0, 1fr) minmax(0, 0.8fr)',
          }}
        >
          <div className="space-y-2">
            {poolGroup ? (
              <div className="flex items-center space-x-3">
                <TokenPairLogo token0={poolGroup.token0} token1={poolGroup.token1} size="md" />
                <div className="flex flex-col gap-1">
                  <div className="flex items-center gap-2 font-medium text-kodiak-text-primary min-w-0 max-w-full">
                    <span className="min-w-0 truncate">
                      {poolGroup.token0.symbol}-{poolGroup.token1.symbol}
                    </span>
                    {positionBadge}
                  </div>
                  <div className="text-xs text-kodiak-text-secondary">
                    {poolGroup.feeTier / 10000}% • {positionIdLabel}
                  </div>
                </div>
              </div>
            ) : null}
          </div>
          <div>
            {rangeDisplay ? (
              rangeDisplay
            ) : showRangePlaceholder ? (
              <div className="text-kodiak-text-secondary text-left">—</div>
            ) : null}
          </div>
          {renderDesktopSharedColumns()}
        </div>
      </div>
    )
  }

  return (
    <div className="border-b-0 last:border-kodiak-border-secondary bg-[#222f42]">
      <div className="sm:hidden pl-6 pr-3 py-3 space-y-2">
        <div className="flex flex-col">
          <div className="flex justify-between gap-2 space-y-3">
            {positionBadge}
            <div className="text-kodiak-text-primary font-medium text-sm">{formatDollarAmount(positionValue)}</div>
          </div>

          {renderMobileRange()}

          {renderMobileDetails('text-xs font-medium text-kodiak-text-primary mb-1')}
        </div>

        {renderActions('flex flex-col gap-2')}
      </div>

      <div
        className="hidden sm:grid items-center py-3 px-6 gap-4"
        style={{
          gridTemplateColumns:
            'minmax(0, 1.55fr) minmax(0, 150px) minmax(0, 1fr) minmax(0, 0.6fr) minmax(0, 1fr) minmax(0, 0.8fr)',
        }}
      >
        <div className="space-y-2 pl-4">
          {positionBadge}
          <div className="text-sm font-medium text-kodiak-text-primary">{positionIdLabel}</div>
        </div>
        <div>
          {rangeDisplay ? (
            rangeDisplay
          ) : showRangePlaceholder ? (
            <div className="text-kodiak-text-secondary text-left">—</div>
          ) : null}
        </div>
        {renderDesktopSharedColumns()}
      </div>
    </div>
  )
}
